import { useEffect, useRef, useState } from "react";
import { api, apiErr } from "@/api";
import Shell from "@/components/Shell";
import { exhibitorNav } from "./ExhibitorDashboard";
import { Spinner, Overline } from "@/components/Brand";
import { useEvent } from "@/context/EventContext";
import { toast } from "sonner";
import { useScrollLock } from "@/hooks/useScrollLock";
import { MapPin, CalendarClock, Send, Check, X, MessageSquare, Clock } from "lucide-react";

export default function Networking() {
  const [tab, setTab] = useState("directory");
  const [dir, setDir] = useState(null);
  const [meetings, setMeetings] = useState([]);
  const [slots, setSlots] = useState([]);
  const [slotInput, setSlotInput] = useState("");
  const [chatWith, setChatWith] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [reqFor, setReqFor] = useState(null);
  useScrollLock(!!reqFor);
  const wsRef = useRef(null);
  const { effectivePhase } = useEvent();
  const isPast = effectivePhase === "post_event";

  const loadDir = () => api.get("/exhibitor/networking/directory").then(({ data }) => setDir(data));
  const loadMeetings = () => api.get("/exhibitor/meetings").then(({ data }) => setMeetings(data));
  const loadSlots = () => api.get("/exhibitor/networking/availability").then(({ data }) => setSlots(data.slots || []));

  useEffect(() => { loadDir(); loadMeetings(); loadSlots(); }, []);

  // WebSocket for realtime chat
  useEffect(() => {
    const token = localStorage.getItem("jjs_token");
    if (!token) return;
    const wsUrl = process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws") + `/api/ws/chat?token=${token}`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;
    ws.onmessage = (e) => {
      const m = JSON.parse(e.data);
      if (m.type === "message") {
        setChatWith((cw) => {
          if (cw && (m.from_exhibitor === cw.id || m.to_exhibitor === cw.id)) {
            setMessages((prev) => prev.some((x) => x.id === m.id) ? prev : [...prev, m]);
          }
          return cw;
        });
      }
    };
    return () => ws.close();
  }, []);

  const openChat = async (ex) => {
    setChatWith(ex);
    setTab("chat");
    const { data } = await api.get(`/exhibitor/messages/${ex.id}`);
    setMessages(data);
  };

  const sendMsg = (e) => {
    e.preventDefault();
    if (!text.trim() || !chatWith) return;
    if (wsRef.current?.readyState === 1) {
      wsRef.current.send(JSON.stringify({ to_exhibitor: chatWith.id, text: text.trim() }));
    } else {
      api.post("/exhibitor/messages", { to_exhibitor: chatWith.id, text: text.trim() }).then(({ data }) => setMessages((p) => [...p, data]));
    }
    setText("");
  };

  const requestMeeting = async (slot) => {
    try {
      await api.post("/exhibitor/meetings", { to_exhibitor: reqFor.id, slot, note: "" });
      toast.success("Meeting requested");
      setReqFor(null); loadMeetings();
    } catch (err) { toast.error(apiErr(err)); }
  };

  const respond = async (m, status) => {
    try { await api.put(`/exhibitor/meetings/${m.id}`, { status }); loadMeetings(); toast.success(`Meeting ${status}`); }
    catch (err) { toast.error(apiErr(err)); }
  };

  const addSlot = async () => {
    if (!slotInput.trim()) return;
    const next = [...slots, slotInput.trim()];
    await api.put("/exhibitor/networking/availability", { slots: next });
    setSlots(next); setSlotInput(""); toast.success("Slot added");
  };
  const removeSlot = async (s) => {
    const next = slots.filter((x) => x !== s);
    await api.put("/exhibitor/networking/availability", { slots: next });
    setSlots(next);
  };

  const TABS = [["directory", "Directory"], ["meetings", "Meetings"], ["availability", "My Slots"], ["chat", "Messages"]];

  return (
    <Shell nav={exhibitorNav}>
      <Overline>Exhibitor Networking</Overline>
      <h1 className="font-display text-3xl gold-text mt-2">Connect with Exhibitors</h1>

      <div className="flex gap-2 overflow-x-auto mt-5 pb-1" style={{ scrollbarWidth: "none" }}>
        {TABS.map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`net-tab-${k}`} className={`whitespace-nowrap text-sm px-4 py-2 rounded-full border transition ${tab === k ? "btn-gold border-transparent" : "glass text-slate-300 border-amber-500/20"}`}>{label}</button>
        ))}
      </div>

      {tab === "directory" && (!dir ? <Spinner /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
          {dir.map((ex) => (
            <div key={ex.id} className="glass rounded-2xl p-4" data-testid={`net-exhibitor-${ex.id}`}>
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-xl bg-cover bg-center shrink-0" style={{ backgroundImage: `url(${ex.logo_url})` }} />
                <div className="min-w-0 flex-1">
                  <div className="text-slate-100 font-medium truncate">{ex.name}</div>
                  <div className="flex items-center gap-1 text-xs text-slate-400"><MapPin size={11} /> {ex.hall} · {ex.booth}</div>
                </div>
              </div>
              <div className="flex gap-2 mt-3">
                <button onClick={() => openChat(ex)} className="flex-1 glass py-2 rounded-lg text-xs text-slate-200 flex items-center justify-center gap-1" data-testid={`net-message-${ex.id}`}><MessageSquare size={13} /> Message</button>
                <button onClick={() => setReqFor(ex)} disabled={isPast} className="flex-1 btn-gold py-2 rounded-lg text-xs flex items-center justify-center gap-1 disabled:opacity-50" data-testid={`net-meet-${ex.id}`}><CalendarClock size={13} /> Meet</button>
              </div>
            </div>
          ))}
        </div>
      ))}

      {tab === "meetings" && (
        <div className="space-y-3 mt-5">
          {meetings.length === 0 ? <p className="text-slate-400 text-center py-12">No meetings yet.</p> : meetings.map((m) => (
            <div key={m.id} className="glass rounded-2xl p-4" data-testid={`meeting-${m.id}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-slate-100 font-medium">{m.direction === "incoming" ? m.from_name : m.to_name}</div>
                  <div className="text-xs text-slate-400 flex items-center gap-1"><Clock size={11} /> {m.slot} · <span className="capitalize">{m.status}</span> · {m.direction}</div>
                </div>
                {m.direction === "incoming" && m.status === "requested" && (
                  <div className="flex gap-2">
                    <button onClick={() => respond(m, "accepted")} className="h-8 w-8 rounded-lg btn-gold flex items-center justify-center" data-testid={`meeting-accept-${m.id}`}><Check size={15} /></button>
                    <button onClick={() => respond(m, "declined")} className="h-8 w-8 rounded-lg glass flex items-center justify-center text-red-300" data-testid={`meeting-decline-${m.id}`}><X size={15} /></button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "availability" && (
        <div className="mt-5">
          <div className="glass rounded-2xl p-4">
            <p className="text-sm text-slate-400 mb-3">Add 22–25 Dec meeting slots other exhibitors can request.</p>
            <div className="flex gap-2">
              <input value={slotInput} onChange={(e) => setSlotInput(e.target.value)} placeholder="2026-12-22 11:00" className="flex-1 bg-black/30 gold-border rounded-xl px-3 py-2.5 text-sm text-slate-100 focus:outline-none" data-testid="slot-input" />
              <button onClick={addSlot} className="btn-gold px-4 rounded-xl" data-testid="slot-add">Add</button>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mt-4">
            {slots.map((s) => (
              <span key={s} className="text-xs px-3 py-1.5 rounded-full bg-amber-500/10 text-amber-300 flex items-center gap-1" data-testid={`slot-${s}`}>{s}<X size={12} className="cursor-pointer" onClick={() => removeSlot(s)} /></span>
            ))}
            {slots.length === 0 && <p className="text-slate-400 text-sm">No slots yet.</p>}
          </div>
        </div>
      )}

      {tab === "chat" && (
        <div className="mt-5">
          {!chatWith ? <p className="text-slate-400 text-center py-12">Pick an exhibitor from the Directory to start chatting.</p> : (
            <div className="glass rounded-2xl p-4" data-testid="chat-panel">
              <div className="font-medium text-slate-100 border-b border-amber-500/15 pb-2 mb-3">{chatWith.name}</div>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {messages.map((m) => (
                  <div key={m.id} className={`flex ${m.from_exhibitor === chatWith.id ? "justify-start" : "justify-end"}`}>
                    <div className={`max-w-[75%] px-3 py-2 rounded-2xl text-sm ${m.from_exhibitor === chatWith.id ? "bg-amber-500/10 text-slate-100" : "btn-gold"}`}>{m.text}</div>
                  </div>
                ))}
                {messages.length === 0 && <p className="text-slate-400 text-sm text-center py-6">Say hello 👋</p>}
              </div>
              <form onSubmit={sendMsg} className="flex gap-2 mt-3">
                <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a message…" className="flex-1 bg-black/30 gold-border rounded-xl px-3 py-2.5 text-sm text-slate-100 focus:outline-none" data-testid="chat-input" />
                <button className="btn-gold px-4 rounded-xl" data-testid="chat-send"><Send size={16} /></button>
              </form>
            </div>
          )}
        </div>
      )}

      {reqFor && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end md:items-center justify-center p-4" onClick={() => setReqFor(null)}>
          <div className="glass rounded-3xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="meeting-request-modal">
            <h3 className="font-display text-2xl gold-text">Request a meeting</h3>
            <p className="text-slate-400 text-sm mt-1">{reqFor.name} · pick a slot</p>
            <div className="flex flex-wrap gap-2 mt-4">
              {(reqFor.slots || []).length === 0 ? <p className="text-slate-400 text-sm">No slots published by this exhibitor yet.</p> :
                reqFor.slots.map((s) => (
                  <button key={s} onClick={() => requestMeeting(s)} className="text-xs px-3 py-2 rounded-xl glass text-slate-200 border border-amber-500/20" data-testid={`req-slot-${s}`}>{s}</button>
                ))}
            </div>
          </div>
        </div>
      )}
    </Shell>
  );
}
